import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  isPrimaryRoute,
  isSecondaryRoute,
  getRouteConfig,
} from "../config/route-config";

export interface BreadcrumbItem {
  path: string;
  i18nKey: string; // Default i18n key
  dynamicLabel?: string; // Custom dynamic label (conversation name, wizard title vb.)
  timestamp: number;
  isHome?: boolean;
}

interface BreadcrumbStore {
  // State
  history: BreadcrumbItem[];
  dynamicLabels: Record<string, string>; // path -> dynamic label mapping

  // Actions
  addToHistory: (
    item: Omit<BreadcrumbItem, "timestamp" | "dynamicLabel">
  ) => void;
  navigateToHistoryItem: (path: string) => void;
  clearHistory: () => void;
  getVisibleHistory: () => BreadcrumbItem[];
  setDynamicLabel: (path: string, label: string) => void; // Set custom label for breadcrumb item
}

const initialState = {
  history: [],
  dynamicLabels: {},
};

export const useBreadcrumbStore = create<BreadcrumbStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Add new page to history (Smart tracking based on route config)
      addToHistory: (item) => {
        const timestamp = Date.now();

        // Route config'i kontrol et
        const config = getRouteConfig(item.path);

        // LAYER route'ları breadcrumb'a ekleme
        if (config && !config) {
          return; // Skip layer routes
        }

        // SECONDARY route'lar history'ye EKLENMEZ! (context'i korur)
        if (isSecondaryRoute(item.path)) {
          return; // Secondary route'lar mevcut history'yi hiç değiştirmez
        }

        // PRIMARY route ise history'yi REPLACE et (sadece 1 primary route)
        if (isPrimaryRoute(item.path)) {
          // PRIMARY route'lar birbirini replace eder!
          // History'de sadece EN SON primary route tutulur

          // Yeni primary route history'yi tamamen replace eder
          const newHistory = [
            {
              ...item,
              timestamp,
            },
          ];

          set({ history: newHistory });
        }
      },

      // Navigate to history item (path'e kadar olan history'yi tut)
      navigateToHistoryItem: (path) => {
        const { history } = get();

        if (path === "/") {
          // Ana sayfaya git - history'yi temizle
          set({ history: [] });
          return;
        }

        // Path'e kadar olan item'ları tut (inclusive)
        const targetIndex = history.findIndex((h) => h.path === path);
        if (targetIndex !== -1) {
          const newHistory = history.slice(0, targetIndex + 1);
          set({ history: newHistory });
        }
      },

      // Clear all history
      clearHistory: () => {
        set({ history: [], dynamicLabels: {} });
      },

      // Get visible history (with home page)
      getVisibleHistory: () => {
        const { history } = get();

        // Her zaman ana sayfa ile başla
        const homeItem: BreadcrumbItem = {
          path: "/",
          i18nKey: "common.home",
          timestamp: 0,
          isHome: true,
        };

        return [homeItem, ...history];
      },

      // Set dynamic label for breadcrumb item
      setDynamicLabel: (path, label) => {
        set((state) => ({
          ...state,
          dynamicLabels: {
            ...state.dynamicLabels,
            [path]: label,
          },
        }));
      },
    }),
    {
      name: "breadcrumb-storage",
      partialize: (state) => ({
        history: state.history,
        dynamicLabels: state.dynamicLabels,
      }),
    }
  )
);
